/**
 * LauncherThumbnailCapturer — registers the launcher-thumbnail capturer from
 * inside the R3F Canvas (ENG-1079), where gl/scene/camera exist.
 *
 * Capture grabs the REAL composited frame off the live canvas via
 * captureStream()+grabFrame() — the same live-canvas grab AssetIcon's hover
 * video uses. This is what makes it correct for Gaussian-splat / OGS characters:
 * their splats are drawn by Spark's own render pass (and the fp16 blit), which a
 * manual `gl.render(scene, camera)` does NOT reproduce (that path captured the
 * ground grid on black — the reported bug). The manual render survives only as a
 * fallback for a browser without ImageCapture (GLB characters render fine there;
 * splats degrade to blank, which the blank-guard then rejects).
 *
 * Nothing runs on the render hot path — the registered fn only executes when a
 * capture is explicitly requested (menu action / one-shot auto-fallback).
 */

import { useThree } from '@react-three/fiber'
import { useEffect } from 'react'
import { setLauncherThumbnailCapturer } from '../lib/launcherThumbnail'
import { thumbnailCropRect, isFrameBlank, THUMB_W, THUMB_H } from '../lib/launcherThumbnailCrop'

// Grab the current composited frame off the canvas as an ImageBitmap, or null
// when the browser can't (no captureStream / no ImageCapture / grab failed).
async function grabCompositedFrame(canvas) {
  if (typeof canvas.captureStream !== 'function' || typeof window === 'undefined' || !('ImageCapture' in window)) {
    return null
  }
  let track = null
  try {
    const stream = canvas.captureStream()
    track = stream.getVideoTracks()[0]
    if (!track) return null
    return await new window.ImageCapture(track).grabFrame()
  } catch (e) {
    console.warn('[launcherThumbnail] grabFrame failed, falling back to gl.render:', e?.message ?? e)
    return null
  } finally {
    if (track) track.stop()
  }
}

export default function LauncherThumbnailCapturer() {
  const { gl, scene, camera } = useThree()

  useEffect(() => setLauncherThumbnailCapturer(async ({ w = THUMB_W, h = THUMB_H } = {}) => {
    const canvas = gl.domElement
    const bitmap = await grabCompositedFrame(canvas)

    const srcW = bitmap ? bitmap.width : canvas.width
    const srcH = bitmap ? bitmap.height : canvas.height
    const crop = thumbnailCropRect(srcW, srcH, w / h)
    if (!crop) { bitmap?.close?.(); return null } // nothing rendered yet

    const out = document.createElement('canvas')
    out.width = w
    out.height = h
    const ctx = out.getContext('2d')
    if (!ctx) { bitmap?.close?.(); return null }

    if (bitmap) {
      ctx.drawImage(bitmap, crop.sx, crop.sy, crop.cw, crop.ch, 0, 0, w, h)
      bitmap.close?.()
    } else {
      // Fallback: force a fresh frame and read it in this tick (works without
      // preserveDrawingBuffer for GLB; a splat comes out blank and is rejected
      // below rather than saved).
      gl.render(scene, camera)
      ctx.drawImage(canvas, crop.sx, crop.sy, crop.cw, crop.ch, 0, 0, w, h)
    }

    // Reject a subject-less frame (splat still decoding, or a too-early grab) so
    // auto-capture retries instead of saving a grid-on-black thumbnail.
    if (isFrameBlank(ctx.getImageData(0, 0, w, h).data, w, h)) return null

    return out.toDataURL('image/jpeg', 0.9)
  }), [gl, scene, camera])

  return null
}
